'use client'

import { useEffect, useRef, type RefObject } from 'react'

/**
 * 2D slab flip controller — a faithful port of the app's slab swivel
 * (owen/grade-page CardGradeScreen.tsx). The whole flat slab rotates on its
 * Y axis; there is NO pitch/tilt. Horizontal drag previews the flip
 * (dragRatio × 180 × 1.5, clamped ±180 from the start angle); release snaps
 * to the nearest face. The grade-screen spring is reproduced exactly:
 * mass 0.6, damping 18, stiffness 220, overshoot-clamped.
 *
 * Output is written as CSS custom properties on the stage element:
 *   --rot         rotation in degrees
 *   --flip-scale  1 on the front face, -1 on the back (un-mirrors content)
 *   --front-o     front image opacity (hard toggle at the 90° edge-on point)
 *   --back-o      back image opacity
 */

const SPRING = { mass: 0.6, damping: 18, stiffness: 220 }
const AXIS_SLOP_PX = 12
const TAP_SLOP_PX = 6

export function useSlabFlip(stageRef: RefObject<HTMLElement | null>, enabled = true) {
  const flipRef = useRef<() => void>(() => {})

  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return
    if (!enabled) {
      stage.style.setProperty('--rot', '0deg')
      stage.style.setProperty('--flip-scale', '1')
      stage.style.setProperty('--front-o', '1')
      stage.style.setProperty('--back-o', '0')
      flipRef.current = () => {}
      return
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

    let rotation = 0 // degrees; 0 = front, 180 = back
    let velocity = 0 // deg/s
    let target: number | null = null

    let dragging = false
    let axisLocked = false
    let pointerId = -1
    let startX = 0
    let startY = 0
    let startRotation = 0
    let moved = 0

    let raf = 0
    let lastTime = 0

    const writeVars = () => {
      const normalized = ((rotation % 360) + 360) % 360
      const isFront = normalized < 90 || normalized > 270
      const s = stage.style
      s.setProperty('--rot', `${rotation.toFixed(2)}deg`)
      s.setProperty('--flip-scale', isFront ? '1' : '-1')
      s.setProperty('--front-o', isFront ? '1' : '0')
      s.setProperty('--back-o', isFront ? '0' : '1')
    }

    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - lastTime) / 1000 || 0.016)
      lastTime = now

      if (target !== null) {
        // Semi-implicit Euler at 1ms substeps for stiff-spring stability
        let remaining = dt
        const h = 0.001
        while (remaining > 0) {
          const step = Math.min(h, remaining)
          const a =
            (-SPRING.stiffness * (rotation - target) - SPRING.damping * velocity) /
            SPRING.mass
          velocity += a * step
          rotation += velocity * step
          remaining -= step
        }
        // overshootClamping: settle once we're essentially there
        if (Math.abs(rotation - target) < 0.1 && Math.abs(velocity) < 1) {
          rotation = target
          velocity = 0
          target = null
        }
      }

      writeVars()

      if (target === null && !dragging) {
        raf = 0
        return
      }
      raf = requestAnimationFrame(tick)
    }

    const wake = () => {
      if (!raf) {
        lastTime = performance.now()
        raf = requestAnimationFrame(tick)
      }
    }

    const springTo = (to: number) => {
      if (reduceMotion.matches) {
        rotation = to
        velocity = 0
        target = null
        writeVars()
        return
      }
      target = to
      wake()
    }

    // Flip to the other face (handleFlipCard)
    const flip = () => {
      const normalized = ((rotation % 360) + 360) % 360
      const isFront = normalized < 90 || normalized > 270
      const nearestBase = Math.round(rotation / 360) * 360
      springTo(isFront ? nearestBase + 180 : nearestBase)
    }
    flipRef.current = flip

    const onPointerDown = (e: PointerEvent) => {
      if (!e.isPrimary) return
      dragging = true
      axisLocked = false
      pointerId = e.pointerId
      startX = e.clientX
      startY = e.clientY
      startRotation = target ?? rotation
      target = null
      velocity = 0
      moved = 0
      wake()
    }

    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerId !== pointerId || !dragging) return
      const dx = e.clientX - startX
      const dy = e.clientY - startY
      moved = Math.max(moved, Math.hypot(dx, dy))

      if (!axisLocked) {
        if (Math.abs(dx) > AXIS_SLOP_PX && Math.abs(dx) > Math.abs(dy)) {
          axisLocked = true
          stage.setPointerCapture(pointerId)
        } else if (Math.abs(dy) > AXIS_SLOP_PX) {
          // Vertical intent — let the page scroll, abandon this drag
          dragging = false
          return
        } else {
          return
        }
      }

      if (reduceMotion.matches) return
      const w = stage.clientWidth || 300
      let deltaDeg = (dx / w) * 180 * 1.5
      deltaDeg = Math.max(-180, Math.min(180, deltaDeg))
      rotation = startRotation + deltaDeg
      wake()
    }

    const endDrag = (e: PointerEvent, cancelled: boolean) => {
      if (e.pointerId !== pointerId || !dragging) return
      dragging = false
      pointerId = -1
      if (stage.hasPointerCapture(e.pointerId)) {
        stage.releasePointerCapture(e.pointerId)
      }

      if (cancelled) {
        springTo(Math.round(rotation / 180) * 180)
        return
      }

      // Tap (negligible movement) flips
      if (moved < TAP_SLOP_PX) {
        flip()
        return
      }

      springTo(Math.round(rotation / 180) * 180)
    }

    const onPointerUp = (e: PointerEvent) => endDrag(e, false)
    const onPointerCancel = (e: PointerEvent) => endDrag(e, true)

    stage.addEventListener('pointerdown', onPointerDown)
    stage.addEventListener('pointermove', onPointerMove)
    stage.addEventListener('pointerup', onPointerUp)
    stage.addEventListener('pointercancel', onPointerCancel)

    writeVars()

    return () => {
      stage.removeEventListener('pointerdown', onPointerDown)
      stage.removeEventListener('pointermove', onPointerMove)
      stage.removeEventListener('pointerup', onPointerUp)
      stage.removeEventListener('pointercancel', onPointerCancel)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [stageRef, enabled])

  return flipRef
}
